import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import MissingWarehouseStockDialog from './MissingWarehouseStockDialog';
import {
  WarehouseStockTemplate,
  deleteWarehouseStockTemplate,
  flattenTemplateSelection,
  loadWarehouseStockTemplates,
  saveWarehouseStockTemplate,
} from '../utils/warehouseStockTemplates';
import {
  createProductsInWarehouse,
  findProductsMissingInWarehouse,
  type MissingWarehouseProduct,
} from '../utils/warehouseStockProvisioning';

/**
 * Depo/Stok sekmesinin taslak bandı: taslak kaydetme, silme ve UYGULAMA akışı.
 *
 * Taslak, kaydedildiği ekranın DEPOSUNU taşımaz. Uygulama sırasında hedef depo
 * kullanıcıya sorulur; taslaktaki kategoriler ve ürünler o depoya yazılır.
 * Ardından ürünlerin hedef depoda tanımı olup olmadığı kontrol edilir ve eksikler
 * için (isteğe bağlı) 1 adetlik tanım açılır — kaynak depodan hiçbir miktar
 * düşülmez.
 */

/** Taslağın uygulanacağı depo seçimi için gereken en küçük depo bilgisi. */
export interface TemplateTargetWarehouse {
  id: string;
  name: string;
}

interface WarehouseStockTemplateBarProps {
  companyId: string | null | undefined;
  isMobile: boolean;
  allWarehouses: TemplateTargetWarehouse[];
  selectedWarehouseIds: string[];
  /** Uygulama diyalogunda ön seçili gelecek depo (açık depo sekmesi). */
  activeWarehouseId?: string | null;
  selectedCategoriesPerWarehouse: { [warehouseId: string]: string[] };
  selectedProductsPerCategory: { [warehouseId: string]: { [categoryId: string]: string[] } };
  usePackagePrices: boolean;
  onApply: (template: WarehouseStockTemplate, targetWarehouseId: string) => void;
}

const WarehouseStockTemplateBar: React.FC<WarehouseStockTemplateBarProps> = ({
  companyId,
  isMobile,
  allWarehouses,
  selectedWarehouseIds,
  activeWarehouseId,
  selectedCategoriesPerWarehouse,
  selectedProductsPerCategory,
  usePackagePrices,
  onApply,
}) => {
  const [templates, setTemplates] = useState<WarehouseStockTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState('');
  const [saveDialogError, setSaveDialogError] = useState<string | null>(null);

  const [applyTemplate, setApplyTemplate] = useState<WarehouseStockTemplate | null>(null);
  const [applyWarehouseId, setApplyWarehouseId] = useState<string>('');
  const [applying, setApplying] = useState(false);

  const [missingProducts, setMissingProducts] = useState<MissingWarehouseProduct[] | null>(null);
  const [missingWarehouse, setMissingWarehouse] = useState<TemplateTargetWarehouse | null>(null);
  const [creatingStocks, setCreatingStocks] = useState(false);

  const [feedback, setFeedback] = useState<{ severity: 'success' | 'info' | 'error'; message: string } | null>(null);

  const refreshTemplates = useCallback(() => {
    setTemplates(companyId ? loadWarehouseStockTemplates(companyId) : []);
  }, [companyId]);

  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const hasAnySelection = selectedWarehouseIds.length > 0;

  const applySelection = useMemo(
    () => (applyTemplate ? flattenTemplateSelection(applyTemplate) : null),
    [applyTemplate]
  );

  const warehouseById = useMemo(() => {
    const map = new Map<string, TemplateTargetWarehouse>();
    allWarehouses.forEach((warehouse) => map.set(warehouse.id, warehouse));
    return map;
  }, [allWarehouses]);

  const handleOpenSaveDialog = () => {
    setSaveDialogName(selectedTemplate?.name || '');
    setSaveDialogError(null);
    setSaveDialogOpen(true);
  };

  const handleConfirmSaveTemplate = () => {
    if (!companyId) return;
    const name = saveDialogName.trim();
    if (!name) {
      setSaveDialogError('Taslak adı boş olamaz.');
      return;
    }
    const saved = saveWarehouseStockTemplate(companyId, {
      name,
      selectedWarehouseIds,
      selectedCategoriesPerWarehouse,
      selectedProductsPerCategory,
      usePackagePrices,
    });
    if (!saved) {
      setSaveDialogError('Taslak kaydedilemedi. LocalStorage dolu olabilir.');
      return;
    }
    refreshTemplates();
    setSelectedTemplateId(saved.id);
    setSaveDialogOpen(false);
  };

  const handleDeleteSelectedTemplate = () => {
    if (!companyId || !selectedTemplate) return;
    if (!window.confirm(`"${selectedTemplate.name}" taslağı silinsin mi?`)) return;
    if (deleteWarehouseStockTemplate(companyId, selectedTemplate.id)) {
      setSelectedTemplateId('');
      refreshTemplates();
    }
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((tpl) => tpl.id === templateId);
    if (!template) return;

    // Hedef depo önerisi: açık depo sekmesi, yoksa ekranda tek depo varsa o.
    const suggested =
      (activeWarehouseId && warehouseById.has(activeWarehouseId) ? activeWarehouseId : '') ||
      (selectedWarehouseIds.length === 1 ? selectedWarehouseIds[0] : '');

    setApplyWarehouseId(suggested);
    setApplyTemplate(template);
  };

  const handleConfirmApply = async () => {
    if (!applyTemplate || !applyWarehouseId || !applySelection) return;

    const warehouse = warehouseById.get(applyWarehouseId) || { id: applyWarehouseId, name: applyWarehouseId };
    setApplying(true);
    try {
      onApply(applyTemplate, applyWarehouseId);

      // Kontrol, ürünler forma yazıldıktan SONRA yapılıyor: kullanıcı diyalogu
      // kapattığında seçim zaten uygulanmış olur, sadece tanım açma adımı atlanır.
      let missing: MissingWarehouseProduct[] = [];
      let checkFailed = false;
      try {
        missing = await findProductsMissingInWarehouse(applySelection.productIds, applyWarehouseId);
      } catch (e) {
        checkFailed = true;
        console.error('[WarehouseStockTemplateBar] Depo stok kontrolü başarısız:', e);
      }

      setApplyTemplate(null);

      if (checkFailed) {
        setFeedback({
          severity: 'info',
          message:
            `Taslak "${warehouse.name}" deposuna uygulandı, ancak ürünlerin bu depoda tanımlı olup ` +
            'olmadığı kontrol edilemedi. Satışta eksik tanımlar otomatik açılır.',
        });
        return;
      }

      if (missing.length === 0) {
        setFeedback({
          severity: 'success',
          message: `Taslak "${warehouse.name}" deposuna uygulandı; ${applySelection.productIds.length} ürünün tamamı bu depoda tanımlı.`,
        });
        return;
      }

      setMissingWarehouse(warehouse);
      setMissingProducts(missing);
    } finally {
      setApplying(false);
    }
  };

  const handleCreateMissingStocks = async () => {
    if (!companyId || !missingWarehouse || !missingProducts?.length) return;
    setCreatingStocks(true);
    try {
      const count = await createProductsInWarehouse(
        companyId,
        missingWarehouse.id,
        missingProducts.map((product) => product.id)
      );
      setFeedback({
        severity: 'success',
        message: `${count} ürün "${missingWarehouse.name}" deposunda 1 adetle tanımlandı. Kaynak depodaki miktarlar değişmedi.`,
      });
      setMissingProducts(null);
      setMissingWarehouse(null);
    } catch (e: any) {
      setFeedback({
        severity: 'error',
        message:
          e?.response?.data?.message ||
          e?.message ||
          'Ürünler depoda tanımlanamadı. Depo/ürün modülünden manuel ekleyebilirsiniz.',
      });
    } finally {
      setCreatingStocks(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 1,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <BookmarkIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="caption" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
            Depo/Stok Taslakları
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
          <Select
            value={selectedTemplateId}
            displayEmpty
            onChange={(e) => handleSelectTemplate(e.target.value as string)}
          >
            <MenuItem value="">
              <em>{templates.length === 0 ? 'Henüz taslak yok' : 'Taslak Seç (Yükle)'}</em>
            </MenuItem>
            {templates.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.id}>
                {tpl.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1}>
          <Tooltip
            title={
              hasAnySelection
                ? 'Mevcut kategori/ürün seçimlerini taslak olarak kaydet'
                : 'Kaydetmek için önce depo/kategori/ürün seçin'
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BookmarkAddIcon fontSize="small" />}
                onClick={handleOpenSaveDialog}
                disabled={!hasAnySelection}
              >
                Taslak Olarak Kaydet
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Seçili taslağı sil">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={handleDeleteSelectedTemplate}
                disabled={!selectedTemplateId}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Taslak kaydetme */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkAddIcon color="primary" />
          Taslak Olarak Kaydet
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Mevcut kategori ve ürün seçimleri isimli bir taslak olarak kaydedilir. Taslak, uygulandığı
            ekranda hangi depoya yazılacağını kullanıcıya sorar; kaynak ekranın deposu taşınmaz. Aynı
            isimli taslak varsa üzerine yazılır. Taslaklar şirket genelindedir ve tüm hızlı satış /
            restoran / paket ekranlarından kullanılabilir.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Taslak Adı"
            size="small"
            value={saveDialogName}
            onChange={(e) => {
              setSaveDialogName(e.target.value);
              setSaveDialogError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConfirmSaveTemplate();
            }}
            error={Boolean(saveDialogError)}
            helperText={saveDialogError || 'Örn: "Mutfak Deposu" veya "Bar Menüsü"'}
            inputProps={{ maxLength: 64 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} color="inherit">
            İptal
          </Button>
          <Button onClick={handleConfirmSaveTemplate} variant="contained" disabled={!saveDialogName.trim()}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Taslak uygulama: hedef depo seçimi */}
      <Dialog
        open={Boolean(applyTemplate)}
        onClose={() => {
          if (!applying) setApplyTemplate(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarehouseIcon color="primary" />
          {applyTemplate ? `Taslağı Uygula: ${applyTemplate.name}` : 'Taslağı Uygula'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Taslaktaki kategoriler ve ürünler, seçtiğiniz deponun altına eklenir. Taslağın kaydedildiği
            depo kopyalanmaz ve o deponun mevcut seçimleri silinmez.
          </Typography>
          {applySelection && (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip size="small" label={`${applySelection.categoryIds.length} kategori`} />
              <Chip size="small" label={`${applySelection.productIds.length} ürün`} />
            </Stack>
          )}
          <Autocomplete
            size="small"
            options={allWarehouses}
            getOptionLabel={(option) => option.name}
            value={warehouseById.get(applyWarehouseId) || null}
            onChange={(_e, value) => setApplyWarehouseId(value?.id || '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={applying}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ürünlerin ekleneceği depo"
                helperText="Ekranda seçili olmayan bir depo seçerseniz depo listeye de eklenir."
              />
            )}
          />
          {applying && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Ürünlerin bu depoda tanımlı olup olmadığı kontrol ediliyor…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyTemplate(null)} color="inherit" disabled={applying}>
            İptal
          </Button>
          <Button
            onClick={() => void handleConfirmApply()}
            variant="contained"
            disabled={!applyWarehouseId || applying}
          >
            Uygula
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hedef depoda tanımı olmayan ürünler */}
      <MissingWarehouseStockDialog
        open={Boolean(missingProducts?.length)}
        warehouseName={missingWarehouse?.name || ''}
        products={missingProducts || []}
        creating={creatingStocks}
        onSkip={() => {
          setMissingProducts(null);
          setMissingWarehouse(null);
        }}
        onCreate={() => void handleCreateMissingStocks()}
      />

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback?.severity || 'info'} onClose={() => setFeedback(null)}>
          {feedback?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WarehouseStockTemplateBar;
